import React, { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getText } from "../configure/utils";
import { useEnv } from "./utils";
import collapse from "../../assets/chat/collapse-white-32.png";
import "./selectchat.style.css";

export default function UnlockResetAD() {
  const { language } = useContext(LanguageContext);
  const { isValid, values, error } = useEnv(["DIALOGFLOW_CX_PROJECT_ID", "DIALOGFLOW_CX_AD_AGENT_ID"]);

  const dfMessengerRef = useRef(null);
  const languageRef = useRef(language || "th");
  // const autoDetectLanguageRef = useRef(true);
  const isInitializedRef = useRef(false);
  const NEW_SESSION = useMemo(
    () => [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#FF9800" },
        mode: "blocking",
        text: getText("df_cx.timeout.button"),
        event: { event: "evt-ad-greeting" },
      },
    ],
    [language]
  );

  const initializeChat = useCallback((messenger) => {
    if (!messenger || isInitializedRef.current) return;

    messenger.startNewSession({ retainHistory: false });
    setTimeout(() => {
      messenger.sendRequest("event", "evt-ad-greeting");
      isInitializedRef.current = true;
    }, 500);
  }, []);

  const handleSentRequest = useCallback((event) => {
    const queryInput = event.detail?.data?.requestBody?.queryInput;
    if (queryInput) {
      queryInput.languageCode = languageRef.current;
    }
  }, []);

  const handleChatSession = useCallback(
    (event) => {
      if (!event.detail.message || !dfMessengerRef.current) return;

      isInitializedRef.current = false;
      initializeChat(dfMessengerRef.current);
    },
    [initializeChat]
  );

  const handleSessionExpired = useCallback(() => {
    if (!dfMessengerRef.current) return;

    dfMessengerRef.current.renderCustomText(getText("df_cx.timeout.message"), true);
    dfMessengerRef.current.renderCustomCard(NEW_SESSION);
  }, [NEW_SESSION]);

  const handleClickButton = useCallback((event) => {
    if (!dfMessengerRef.current || !event?.detail) return;
    const messenger = dfMessengerRef.current;

    const { event: evt, text } = event.detail;
    // If already have event in button
    if (evt) {
      messenger.renderCustomText(text, false);
    } else if (text) {
      messenger.renderCustomText(text, false);
      messenger.sendRequest("query", text);
    }
  }, []);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    const messenger = document.querySelector(".adUnlockReset df-messenger");
    if (!messenger) return;

    const bubble = messenger.querySelector("df-messenger-chat-bubble");
    if (!bubble) return;
    bubble.openChat();

    dfMessengerRef.current = messenger;
    const timeoutId = initializeChat(messenger);

    return () => {
      clearTimeout(timeoutId);
      isInitializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const eventHandlers = {
      "df-request-sent": handleSentRequest,
      "df-session-expired": handleSessionExpired,
      "df-button-clicked": handleClickButton,
      "df-ad-clear-chat-session": handleChatSession,
    };

    Object.entries(eventHandlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [handleSentRequest, handleSessionExpired, handleClickButton, handleChatSession]);

  if (!isValid) {
    console.error("UnlockResetAD configuration is invalid:", error);
    return null;
  }

  return (
    <div className="adUnlockReset">
      <df-messenger project-id={values.DIALOGFLOW_CX_PROJECT_ID} agent-id={values.DIALOGFLOW_CX_AD_AGENT_ID} allow-feedback="all" location="asia-southeast1">
        <df-messenger-chat-bubble
          chat-title="SANDEE Chatbot"
          chat-subtitle={getText("ad_unlock_reset.buttonText")}
          allow-fullscreen="always"
          placeholder-text={getText("placeholdertext")}
          chat-collapse-icon={collapse}
        />
      </df-messenger>
    </div>
  );
}
